/**
 * Tipos de dominio compartidos cliente/servidor.
 * Reflejan el schema de Postgres en infra/postgres/migrations/001_initial.sql.
 */
import type { OrigenMonedaObra } from './monedaObra.js';

// ============ Tenancy ============

export type TenantStatus = 'active' | 'suspended' | 'archived';
export type ProgramId = 'supernova' | 'adlic' | 'novalight' | string;
export type SubscriptionStatus = 'active' | 'trial' | 'past_due' | 'canceled';
export type TenantRole = 'owner' | 'admin' | 'member' | 'viewer';

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  polarisUrl?: string | null;
  status: TenantStatus;
  contactEmail?: string | null;
  createdAt: string;
}

export interface Program {
  id: ProgramId;
  name: string;
  description?: string | null;
  currentVersion?: string | null;
  active: boolean;
}

export interface TenantProgram {
  id: string;
  tenantId: string;
  programId: ProgramId;
  status: SubscriptionStatus;
  features: Record<string, unknown>;
  seats: number;
  startedAt?: string | null;
  expiresAt?: string | null;
}

// ============ Users ============

export interface User {
  id: string;
  email: string;
  username: string;
  name: string;
  avatarUrl?: string | null;
  createdAt: string;
  lastLoginAt?: string | null;
}

export interface TenantUserMembership {
  tenantId: string;
  userId: string;
  role: TenantRole;
  joinedAt: string;
}

// ============ Auth ============

export interface AuthSession {
  accessToken: string;
  user: User;
  tenant: Tenant;
  membership: TenantUserMembership;
  programs: TenantProgram[];
}

export interface LoginRequest {
  username: string;
  password: string;
  /** Si el user pertenece a varios tenants y no se manda → backend devuelve 300 con lista. */
  tenantSlug?: string;
}

// ============ Projects ============

export type ProjectStatus = 'active' | 'archived' | 'deleted';
export type DigestStatus = 'pending' | 'indexing' | 'ready' | 'failed';

export interface Project {
  id: string;
  tenantId: string;
  ownerUserId: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  digestStatus: DigestStatus;
  totalFiles: number;
  totalSizeMb: number;
  obraAnalysis?: ObraAnalysisResult | null;
  createdAt: string;
  updatedAt: string;
  /** True si es el proyecto de ejemplo (demo) precargado: visible para todos los
   *  tenants y de solo lectura (no se puede modificar ni consume tokens). */
  isExample?: boolean;
  /** Stats de la última cotización del proyecto (presentes en el listado). */
  quoteConcepts?: number | null;
  quoteConceptsPriced?: number | null;
  quotedPct?: number | null;
  quotedAmount?: number | null;
  /** True si la última cotización ya fue importada a OPUS (m1). */
  exportedToOpus?: boolean;
  /** Disciplina de obra: contexto editado (project_obra_context) → fallback IA. */
  disciplina?: string | null;
  /** Id de la última cotización del proyecto (para enlazar Detalle → Cotización/APUs). */
  latestQuoteId?: string | null;
  /** Estado de esa última cotización. Se usa para NO reescribir la selección de
   *  archivo/catálogos de un presupuesto ya armado al entrar a verlo. */
  latestQuoteStatus?: string | null;
  /** Vista previa del plano principal del proyecto (solo en la lista de OPUS ci); null si ninguno tiene. */
  portadaUrl?: string | null;
  /** Moneda de la obra (ISO 4217) resuelta por evidencia; `null` = no se sabe.
   *  Ver `monedaObra.ts`: NUNCA es un default. */
  moneda?: string | null;
  /** De dónde salió esa moneda: del contexto, de los catálogos, o no se sabe. */
  monedaOrigen?: OrigenMonedaObra;
}

export type FileClassification =
  | 'bases'
  | 'catalogo'
  | 'plano'
  | 'contrato'
  | 'especificacion'
  | 'anexo'
  | 'memoria'
  | 'otro';

export type ParseStatus = 'pending' | 'parsing' | 'ok' | 'failed' | 'skipped';

export interface ProjectFile {
  id: string;
  tenantId: string;
  projectId: string;
  originalName: string;
  storageKey: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  contentHash?: string | null;
  classification?: FileClassification | null;
  parseStatus: ParseStatus;
  parseError?: string | null;
  parsedPages?: number | null;
  /** Conceptos reales detectados en el texto parseado (señal para proponer
   * el catálogo de conceptos por default). Solo lo puebla el endpoint de
   * detalle del proyecto. */
  conceptsDetected?: number | null;
  ocrUsed: boolean;
  uploadedAt: string;
  parsedAt?: string | null;
}

// ============ Conversations ============

export type MessageRole = 'system' | 'user' | 'assistant' | 'tool';

export interface Citation {
  fileId: string;
  fileName: string;
  chunkId: number;
  page?: number | null;
}

export interface Message {
  id: string;
  conversationId: string;
  role: MessageRole;
  content: string;
  citations?: Citation[] | null;
  toolCalls?: ToolCall[] | null;
  tokenUsage?: TokenUsage | null;
  createdAt: string;
}

export interface ToolCall {
  id: string;
  name: string;
  args: Record<string, unknown>;
  result?: unknown;
  status: 'pending' | 'running' | 'ok' | 'failed';
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  costUsd?: number;
}

export interface Conversation {
  id: string;
  projectId: string;
  userId: string;
  title?: string | null;
  createdAt: string;
  updatedAt: string;
}

// ============ Quotes ============

export type QuoteStatus = 'running' | 'paused' | 'completed' | 'failed';

/**
 * DE DÓNDE SALIÓ UN RENGLÓN DE LA HOJA DE PRESUPUESTO.
 *
 * `documento` (o el campo ausente) = se leyó del documento, con su descripción y su
 * cantidad completas. Es el caso normal y no se marca en ninguna parte.
 *
 * `conteo_pagina` = el renglón NO se pudo leer del documento, ni siquiera volviendo
 * sobre su página: lo único que se tiene de él es lo que se vio al mirar la página
 * (migración 081) — la descripción cortada a unas 60–80 letras y una cantidad que
 * puede no ser la del documento. Entra a la hoja igual, marcado, porque un renglón
 * que falta en un catálogo de licitación no se ve y puede dejar una propuesta afuera;
 * uno marcado se ve y se corrige. Nunca se le busca precio solo (ver
 * `apps/worker/src/jobs/renglonesDelConteo.ts`).
 */
export type RenglonOrigen = 'documento' | 'conteo_pagina';

/** Un renglón que el documento muestra y la lectura no pudo recuperar. */
export interface RenglonSinLeer {
  /** Página del documento donde está. */
  pagina: number;
  clave: string | null;
  /** Cortada: es lo que se alcanzó a ver de la página, no la descripción completa. */
  descripcion: string;
  unidad: string | null;
  cantidad: number | null;
}

/**
 * LA HOJA NO QUEDÓ COMPLETA, y con nombre y apellido: qué renglones y en qué páginas.
 *
 * Se guarda en `quotes.hoja_incompleta` (migración 083). NULL = la hoja cerró contra
 * el conteo de las páginas, o no había con qué compararla.
 *
 * `aviso` viene YA REDACTADO para el usuario (es-MX, sin jerga): lo arma el worker al
 * terminar de leer, y la UI lo muestra tal cual. Acá no entra ningún dato interno
 * (modelos, proveedores, tokens): esto se le muestra al cliente.
 */
export interface HojaIncompleta {
  /** Páginas donde falta algo, ordenadas. */
  paginas: number[];
  /** Cuántos renglones entraron marcados. */
  total: number;
  renglones: RenglonSinLeer[];
  aviso: string;
}

/**
 * LA HOJA SALIÓ CON LA ESTRUCTURA EN DUDA (migr 092, AI-59).
 *
 * Distinto de `HojaIncompleta`: esa habla de RENGLONES que no se pudieron leer, con sus
 * páginas y su flujo de revisión. Esta habla de la FORMA de la hoja — se perdieron las
 * partidas — y sus renglones están perfectos. Mezclarlas dejaría un aviso pidiéndole al
 * cotizador revisar renglones que no tienen nada.
 *
 * `aviso` viene YA REDACTADO para el usuario (es-MX, sin jerga) y la UI lo muestra tal
 * cual. Nada interno: ni modelos, ni proveedores, ni tokens.
 */
export interface EstructuraDudosaGuardada {
  /**
   * Por qué se dudó. `LECTURAS_NO_COINCIDEN` faltaba acá aunque el worker ya lo escribía
   * (2026-08-26): el tipo compartido se había quedado atrás del que produce el dato.
   */
  motivos: Array<'HOJA_PLANA' | 'AGRUPADOR_ES_UN_CONCEPTO' | 'LECTURAS_NO_COINCIDEN'>;
  conceptos: number;
  agrupadores: number;
  /** El título que repetía el texto de un renglón, si fue ese el caso. */
  agrupadorDuplicado?: string;
  /**
   * TODAS las partidas cuyo texto también es el de un renglón, con su clave.
   * `mismaClave` marca la señal fuerte: no es un título parecido a un renglón, es el
   * renglón mismo al que el documento no le puso unidad ni cantidad, y por eso nunca
   * recibe precio.
   */
  agrupadoresSospechosos?: Array<{ clave?: string; descripcion: string; mismaClave: boolean }>;
  /** true = una segunda lectura repuso la estructura. */
  resuelta: boolean;
  /** Cuántas partidas repuso esa segunda lectura. */
  partidasRepuestas?: number;
  aviso: string;
  /**
   * El encabezado del aviso, redactado en el worker junto al resto del texto.
   *
   * Opcional porque las hojas guardadas antes del 2026-08-26 no lo traen: la pantalla cae
   * al texto de siempre para esas. Nace porque el título estaba fijo en la pantalla y
   * decía "quedó sin sus partidas" para cualquier sospecha — incluso con 499 partidas
   * bien armadas.
   */
  titulo?: string;
}

/**
 * EL TÍTULO DEL AVISO, redactado acá y no en la pantalla.
 *
 * Estaba fijo en `QuotePage.tsx`: decía "Este presupuesto quedó sin sus partidas" para
 * CUALQUIER sospecha. El 2026-08-26 un presupuesto con 499 partidas y 2.349 vínculos
 * padre-hijo perfectos mostró ese título porque un solo agrupador repetía el texto de un
 * renglón. El cotizador leyó que había perdido toda su estructura y estuvo a punto de
 * rearmar la hoja — y rearmar SÍ destruye trabajo.
 *
 * Vive acá y no junto al cuerpo del aviso porque lo necesitan los DOS lados: el worker al
 * guardar, y la pantalla como respaldo para las hojas anteriores al 2026-08-26, que no
 * traen el campo. Si estuviera solo en el worker, esas hojas seguirían mostrando el cartel
 * equivocado hasta que alguien las regenerara — y regenerar destruye trabajo.
 */
export function tituloDeEstructura(
  s: Pick<EstructuraDudosaGuardada, 'motivos' | 'agrupadoresSospechosos'>,
  resuelta: boolean,
  /**
   * Cuántas partidas se repusieron DE VERDAD. Existe porque "resuelta" tapaba dos casos
   * distintos y el cartel mentía: el 2026-09-01 un cliente leyó "Las partidas de este
   * presupuesto se repusieron" sobre una hoja donde no se repuso ninguna —la segunda
   * opinión no había encontrado más, y eso se marcaba como resuelto igual—. Sin este dato
   * no hay forma de distinguir "lo arreglamos" de "no había nada que arreglar".
   */
  partidasRepuestas = 0,
): string {
  // RESUELTA = NO SE DICE NADA, y el número de partidas repuestas tampoco.
  //
  // El cartel decía "Las partidas de este presupuesto se repusieron" y Sebastián lo bajó
  // con una frase exacta: "tiene cero información para un usuario". Tenía razón, y la
  // versión que lo reemplazaba —"Se repusieron 5 partidas que faltaban"— era más honesta
  // pero igual de inútil: un cotizador no sabe qué es reponer una partida ni qué hacer con
  // eso. Peor: anunciarle que arreglamos algo que él nunca vio roto le siembra la duda de
  // si la hoja está mal.
  //
  // Cuando está arreglado, el cotizador abre la hoja y ve sus secciones en su lugar. El
  // dato NO se pierde: `estructura_dudosa` sigue guardando `partidasRepuestas` en la base,
  // que es lo que nos sirve a nosotros para medir cuántas hojas salen así.
  //
  // Es además el criterio que ya estaba escrito acá al lado para el otro caso: si ya está
  // arreglado, contárselo es ruido sobre algo que no puede accionar.
  if (resuelta) return '';
  void partidasRepuestas;
  if (s.motivos.includes('HOJA_PLANA')) return 'Este presupuesto quedó sin sus partidas';
  if (s.motivos.includes('AGRUPADOR_ES_UN_CONCEPTO')) {
    // El número va en el título: "una partida" cuando son trece hace que el cotizador
    // subestime lo que tiene que revisar.
    const sinCantidad = (s.agrupadoresSospechosos ?? []).filter((a) => a.mismaClave).length;
    if (sinCantidad === 1) return 'Hay 1 renglón sin unidad ni cantidad';
    if (sinCantidad > 1) return `Hay ${sinCantidad} renglones sin unidad ni cantidad`;
    return 'Hay una partida que conviene revisar';
  }
  return 'Conviene revisar las partidas de este presupuesto';
}

export interface QuoteItem {
  codigo?: string;
  descripcion: string;
  unidad?: string;
  cantidad: number;
  precioUnitario?: number;
  importe?: number;
  insumos?: QuoteInsumo[];
  matched: boolean;
  source?: 'template' | 'ecostos' | 'ai' | 'manual';
}

export interface QuoteInsumo {
  clave?: string;
  descripcion: string;
  unidad?: string;
  cantidad: number;
  precioUnitario?: number;
  source?: 'ecostos' | 'ai';
}

export interface Quote {
  id: string;
  projectId: string;
  status: QuoteStatus;
  sourceFileId?: string | null;
  totalItems?: number | null;
  processedItems: number;
  totalAmount?: number | null;
  excelStorageKey?: string | null;
  createdAt: string;
  updatedAt: string;
  /** True si es la cotización del proyecto de ejemplo (demo, solo lectura). */
  isExample?: boolean;
}

// ============ Obra analysis ============

export interface ObraAnalysisResult {
  entidad?: string;
  licitacionNum?: string;
  objetoObra?: string;
  resumen?: string;
  fechaApertura?: string;
  fechaFallo?: string;
  plazoEjecucion?: string;
  garantiaCumplimiento?: string;
  observaciones?: string[];
  archivosClasificados?: Record<FileClassification, number>;
}

// ============ Junta de aclaraciones (RFI) ============
// Espejo del output del pipeline `apps/worker/src/junta/` (tipos.ts). Los nombres
// de campo se mantienen EXACTOS (incluido el mixto camel/snake) porque el JSON se
// guarda tal cual lo emite el pipeline y el frontend lo lee sin transformar.

export type JuntaEstado = 'idle' | 'running' | 'completed' | 'failed';
export type JuntaTipoConsulta =
  | 'tecnica'
  | 'economica'
  | 'plazo'
  | 'administrativa'
  | 'contradiccion';
export type JuntaDestino = 'escrito' | 'anexo';
export type JuntaPrioridad = 'alta' | 'media' | 'baja';

export interface JuntaVeredictoPortero {
  responde: boolean;
  donde: string;
  respuesta: string;
  motivo: string;
}

export interface JuntaConsulta {
  tipo: JuntaTipoConsulta;
  tema: string;
  pagina: number;
  cita: string;
  pregunta: string;
  impacto: string;
  origen?: string;
  archivo?: string;
  agrupa?: number;
  paginas?: number[];
  citas?: string[];
  paginas_verificadas?: number[];
  destino?: JuntaDestino;
  prioridad?: JuntaPrioridad;
  motivoClasificacion?: string;
  veredicto?: JuntaVeredictoPortero;
  rubro?: string;
  subrubro?: string | null;
}

/** Un rubro de obra del anexo (leído del índice del pliego). */
export interface JuntaRubro {
  titulo: string;
  desde: number;
  cantidad: number;
}

/** El resultado completo del pipeline: los dos documentos + diagnóstico. */
export interface JuntaResultado {
  /** Se presenta a la convocante, ordenado por prioridad. */
  escrito: JuntaConsulta[];
  /** Interno del equipo de estudios, por rubro. */
  anexo: JuntaConsulta[];
  /** Diagnóstico: lo que el propio pliego ya respondía (no va al cliente). */
  resueltas: JuntaConsulta[];
  rubros: JuntaRubro[];
  numeros: Record<string, number>;
}

/** Estado + resultado de la junta de un proyecto (una fila por proyecto). */
export interface ProjectJunta {
  projectId: string;
  status: JuntaEstado;
  result: JuntaResultado | null;
  error: string | null;
  generatedAt: string | null;
}

// ============ Planos (DWG/DXF → SVG tagueado, "de un plano a la HP") ============

/**
 * Un tipo de bloque del plano y sus instancias, agrupadas por nombre efectivo
 * limpio, para "seleccionar todas las iguales" en el visor.
 */
export interface PlanoBloque {
  /** Nombre efectivo limpio del bloque (sin prefijos de xref / anónimos). */
  nombre: string;
  /** Nombre crudo tal como vino en el DXF (para diagnóstico). */
  nombre_crudo: string;
  /** El bloque venía anónimo/dinámico y se resolvió al nombre base. */
  anonimo: boolean;
  /** Cuántas instancias (INSERTs) tiene este tipo de bloque. */
  n: number;
  /** Capas donde aparecen sus instancias. */
  capas: string[];
  /** Ids de cada instancia (matchean los `data-id` del SVG). */
  ids: string[];
  /** Ancho representativo del bbox de una instancia (mediana), en unidades del dibujo. */
  ancho?: number;
  /** Alto representativo del bbox de una instancia (mediana), en unidades del dibujo. */
  alto?: number;
  /**
   * DWG/DXF con varias vistas (paso 7): `n` e `ids` cuentan SOLO las piezas de las vistas que suman
   * (las plantas); `n_total` cuenta todas (también las de elevaciones/cortes) y `por_vista` desglosa
   * por id de vista (`PlanoVista.id`). Ausentes en inventarios viejos o con una sola vista.
   */
  n_total?: number;
  por_vista?: Record<string, number>;
  /** Paso 4: bloques que trae adentro cada pieza (un baño con su inodoro y su lavabo). */
  contiene?: Record<string, number>;
  /** Paso 4: cuántas inserciones son MINSERT (filas × columnas; `n` ya cuenta todas sus piezas). */
  minsert?: number;
}

/**
 * Una VISTA dentro de un archivo DWG/DXF (paso 7 del plan de medición DWG): una isla de geometría del
 * modelspace (planta baja, planta alta, una elevación, un corte). La arma el lector sin IA.
 *
 * - `tipo`: 'planta' = tiene abatimientos de puerta (reconocidos por su geometría); 'otra' = no los tiene
 *   mientras otra vista sí (elevación, corte, detalle); 'sin_clasificar' = ninguna vista del archivo
 *   tiene abatimientos, así que no se distinguen (suman todas, como antes).
 * - `suma`: si sus cantidades entran a las de planta (`PlanoCapa` y `PlanoBloque.n`). 'otra' no suma.
 * - `rotulo`: el texto más alto que queda fuera de toda vista y más cerca de ella ("PLANTA BAJA"),
 *   leído tal cual (sin interpretar). null si no hay.
 */
export interface PlanoVista {
  /** 'v1', 'v2'… de izquierda a derecha. Lo usan `por_vista` de capas y bloques. */
  id: string;
  rotulo: string | null;
  tipo: 'planta' | 'otra' | 'sin_clasificar';
  /**
   * Qué es la vista según las señales del dibujo (sin leer sus textos): 'planta' (puertas con abatimiento),
   * 'corte' (elevación o corte: entrepisos en sus cotas verticales, junto a una planta), 'detalle' (más
   * chica que un entrepiso; deja de sumar si hay otra vista que es la obra) o null (no se puede decir: sin
   * puertas abatibles, una planta y un corte no se separan por geometría). Opcional: inventarios viejos.
   */
  clase?: 'planta' | 'corte' | 'detalle' | null;
  /** Por qué se clasificó así (o por qué no se pudo), en español, para el staff. */
  clase_motivo?: string | null;
  suma: boolean;
  /** Caja [x0, y0, x1, y1] en unidades del dibujo. */
  caja: [number, number, number, number];
  n_entidades: number;
  /** Tamaño en metros (null si la escala es dudosa). */
  ancho_m: number | null;
  alto_m: number | null;
  /** Solo en 'otra': alturas de entrepiso leídas de sus cotas verticales, de abajo hacia arriba (paso 9). */
  niveles?: PlanoNivelVista[];
  /** Solo en plantas: su altura de entrepiso y de dónde sale (paso 9). */
  altura?: PlanoAlturaDeVista;
}

/** Un entrepiso leído de las cotas verticales de una elevación o un corte, en metros. */
export interface PlanoNivelVista {
  /** Desde / hasta, medidos desde la cota más baja de la vista. */
  desde_m: number;
  hasta_m: number;
  altura_m: number;
}

/**
 * Altura de entrepiso de una planta. `origen`:
 *  - 'cota_de_elevacion': leída de las cotas de la vista `vista_fuente` (hay un solo entrepiso, o todos
 *    miden lo mismo: no importa cuál es de cuál planta);
 *  - 'por_desempatar': la elevación trae entrepisos distintos (`candidatas_m`, de abajo hacia arriba) y el
 *    dibujo no dice cuál es de cada planta → se decide con los rótulos de las vistas (IA) o el usuario;
 *  - 'sin_dato': el archivo no trae una elevación o un corte con cotas de entrepiso.
 * Nunca un valor típico: sin dato, `altura_m` es null.
 */
export interface PlanoAlturaDeVista {
  altura_m: number | null;
  origen: 'cota_de_elevacion' | 'por_desempatar' | 'sin_dato';
  vista_fuente: string | null;
  candidatas_m: number[];
  /** Una frase (es-MX) de cómo se obtuvo. */
  nota: string;
}

/** Medidas de una capa dentro de UNA vista (mismas unidades que `PlanoCapa`: unidades del dibujo). */
export interface PlanoCapaEnVista {
  vista: string;
  n_entidades: number;
  longitud: number;
  area: number;
  corrido: number;
  espesor: number | null;
  doble_trazo_conf: number;
  doble_trazo_pares: number;
  /** Recintos cerrados en esa vista (ya en m²). */
  recintos_medidos?: { medidos: { n: number; area_m2: number }; aproximados: { n: number; area_m2: number } };
  eje_bruto?: number;
  vanos?: PlanoVano[];
  eje_por_huella?: number;
  otros_pares?: { n: number; largo: number } | null;
  /** Área reconstruida (aproximada) de los recintos de esta vista, unidades² del dibujo, y su cobertura. */
  area_recintos?: number;
  recintos_conf?: number;
  /** La misma decisión de medida que la capa completa, para esta vista sola (paso 3). */
  cuantificacion?: PlanoCuantificacion;
  /** Eje tal como está dibujado, si hubo que quitarle muro dibujado encima de una ventana. */
  corrido_dibujado?: number;
}

/**
 * IFC: el tipo de una capa tal como lo declara el modelo (`PlanoCapa.tipoDelModelo`). `origen` = de dónde
 * salió el nombre (IfcTypeObject, ObjectType de la pieza, nombre de la pieza o la clase); `uso` = el
 * PredefinedType del grupo ('' = no lo declara o declara varios).
 */
export interface PlanoTipoDelModelo {
  nombre: string;
  origen: 'tipo' | 'objectType' | 'nombre' | 'clase';
  uso: string;
  /**
   * Espesor declarado por el IFC (capas de material del tipo, o cantidad base «Width»), en mm redondeado;
   * null/ausente si no lo trae. Entra a la identidad del tipo cuando los dos lados lo declaran (24 ≠ 25).
   */
  espesorMm?: number | null;
}

/**
 * Una capa del plano y su geometría no-bloque, para "seleccionar toda la capa"
 * (muros/pisos/vanos: geometría suelta, no bloques).
 */
export interface PlanoCapa {
  capa: string;
  n_entidades: number;
  tipos: string[];
  /**
   * Categoría para agrupar capas en subniveles (IFC/BIM): la clase del elemento (p. ej.
   * 'IfcWallStandardCase') bajo la que cuelgan sus tipos/familias. Opcional: el DWG/DXF no la trae
   * (sus capas son planas), y ahí el panel las lista sin agrupar.
   */
  categoria?: string;
  /**
   * IFC: el tipo tal como lo declara el modelo, SIN los sufijos de la etiqueta (« · USO», « · clase»), para
   * reconocerlo en otros proyectos («Conceptos por tipo», memoriaDeTipos.ts). Ausente = inventario viejo o del
   * servidor: el tipo se arma como siempre. Solo IFC.
   */
  tipoDelModelo?: PlanoTipoDelModelo;
  /**
   * Cantidades REALES leídas del modelo BIM (IFC), sumadas sobre los elementos de este tipo, en
   * unidades del proyecto (metros). El IFC 3D las trae directas (área/volumen/longitud), a diferencia
   * del DWG 2D donde el área hay que derivarla con la altura. Opcional: solo IFC la puebla.
   */
  medidas?: {
    /** Número de elementos del tipo (piezas). */
    conteo: number;
    /** Área total (m²) — muros, losas, cristales… */
    area?: number;
    /** Longitud total (m) — elementos lineales (muros, vigas). */
    longitud?: number;
    /** Volumen total (m³) — concreto, etc. */
    volumen?: number;
    /**
     * Peso total (kg) — IFC: el que declara el modelo; si no, volumen × densidad del material (la del
     * modelo o la de su familia, de la calibración). Ausente = no se pudo pesar (material desconocido).
     */
    peso?: number;
    /**
     * true = las cantidades NO las declara el modelo (sin Qto): se ESTIMARON de la geometría de la
     * malla (bbox/volumen real). Es un derivado real, pero aproximado — la UI lo avisa.
     */
    estimadoGeometria?: boolean;
    /**
     * CUÁLES magnitudes se estimaron de la geometría (IFC). Puede ser solo una: un cabrio de ArchiCAD
     * declara área y volumen pero ningún largo que sirva, y el largo se mide de su malla. Vacío/ausente
     * = todo lo declara el modelo. `estimadoGeometria` sigue siendo "alguna se estimó".
     */
    magnitudesEstimadas?: ('area' | 'longitud' | 'volumen' | 'peso')[];
    /**
     * IFC: la magnitud por la que se presupuesta el tipo ("la medida que manda"), decidida por el visor
     * con la FORMA de sus piezas y lo que el modelo dice de ellas (ver medicionIfc: `medidaQueManda`).
     * Ausente en inventarios viejos o del servidor.
     */
    manda?: 'area' | 'longitud' | 'volumen' | 'conteo';
    /** IFC: de dónde salió el volumen de sus piezas (lo declara el modelo, su malla sólida o su perfil × largo). */
    origenesVolumen?: ('declarado' | 'malla' | 'perfil')[];
    /** IFC: piezas que quedaron SIN volumen ni peso, y por qué (su malla no es un sólido y no hay de dónde medirlo). */
    sinVolumen?: { piezas: number; motivo: string };
    /**
     * IFC: piezas cuyo peso salió del material que la IA reconoció por su NOMBRE (el modelo no decía de
     * qué material son). Por material; el peso de esas piezas está marcado como estimado.
     */
    pesosDeducidos?: { material: string; tipo: import('./materialIfcDeducido.js').TipoMaterialIfc; piezas: number }[];
  };
  /**
   * Instancias INDIVIDUALES del tipo (IFC/BIM), para poder seleccionar/deseleccionar una a una en el
   * visor 3D y recalcular la cantidad sobre las que realmente quedan incluidas (sacar 1 de 13 → 12 y
   * su medida menos). `id` = expressID del elemento IFC (identidad estable dentro del modelo); cada
   * instancia trae su propia medida ya en metros (conteo implícito = 1). `medidas` (arriba) es la suma
   * de TODAS; esta lista permite rehacer la suma con un subconjunto. Solo IFC lo puebla; el DWG 2D
   * selecciona por bloque (ids del SVG) y no la trae.
   *
   * `nombre` = nombre/marca REAL de la pieza leído del modelo (Tag o Name del elemento IFC), cuando
   * existe y aporta (una marca "P-01", no el id interno ni el patrón "Tipo:Tipo:id" de Revit). Si el
   * modelo no trae nombre útil, queda vacío y la UI muestra el tipo + índice ("Muro 1", "Puerta 2").
   */
  instancias?: {
    id: number;
    nombre?: string;
    area?: number;
    longitud?: number;
    volumen?: number;
    /** Peso (kg) de la pieza (ver `medidas.peso`). */
    peso?: number;
    /**
     * Números generadores: la caja de la pieza en m — largo y ancho en planta (su rectángulo mínimo) y alto
     * (vertical). Con ella el generador muestra la cuenta (largo × alto, largo × ancho, la sección).
     */
    dims?: { largo: number; ancho: number; alto: number };
    /**
     * GlobalId del IFC: la identidad que sobrevive entre revisiones del modelo (el expressID cambia al reexportar).
     * Con ella «Cambió el plano» empareja las piezas de dos revisiones.
     */
    gid?: string;
    /** Centro de la caja de la pieza en m [x, y, z] (y = vertical, como web-ifc), redondeado al mm. */
    centro?: [number, number, number];
    /** Puertas y ventanas (expressID) que llenan los vanos de la pieza, según el modelo (IfcRelVoids/IfcRelFills). */
    alojados?: number[];
    /** true = alguna medida de esta pieza se estimó de la geometría (el modelo no la declara). */
    estimadoGeometria?: boolean;
    /** Cuáles se estimaron (ver `medidas.magnitudesEstimadas`). */
    magnitudesEstimadas?: ('area' | 'longitud' | 'volumen' | 'peso')[];
    /** De dónde salió su volumen (ver `medidas.origenesVolumen`). */
    origenVolumen?: 'declarado' | 'malla' | 'perfil';
    /** Sin volumen ni peso: el motivo, para la persona (ver `medidas.sinVolumen`). */
    sinVolumen?: string;
    /** Su peso salió del material reconocido por su nombre (ver `medidas.pesosDeducidos`). */
    pesoDeducido?: { material: string; tipo: import('./materialIfcDeducido.js').TipoMaterialIfc };
    /**
     * Lo que el MODELO no dice de la pieza, para «¿Este plano sirve para presupuestar?»
     * (`chequeoDelPlano`): sin tipo (ni IfcTypeObject ni ObjectType), sin piso (no está contenida en un
     * IfcBuildingStorey; los niveles deducidos de las losas no cuentan) y sin material asociado.
     */
    sinTipo?: true;
    sinNivel?: true;
    sinMaterial?: true;
  }[];
  /**
   * Lo que el propio modelo IFC dice del tipo, leído del estándar (no de su nombre): el
   * PredefinedType de la clase, los materiales asociados y el IsExternal de su Pset_*Common. Lo
   * lee el visor 3D de una pieza representativa del tipo. Sirve para nombrarlo en español
   * ("Muro exterior 30 cm") y para que la sugerencia de conceptos no dependa del nombre del tipo,
   * que puede venir en otro idioma o ser un código del programa. Opcional: solo IFC.
   */
  ifc?: {
    predefinido?: string | null;
    materiales?: string[];
    esExterior?: boolean | null;
    /**
     * Ancestros de la clase en el esquema IFC, del más cercano al más lejano (IfcPipeSegment →
     * IfcFlowSegment, IfcDistributionFlowElement, IfcDistributionElement, IfcElement…). Con ellos se
     * agrupa y se mide lo que la tabla de etiquetas no nombra (ver etiquetasIfc).
     */
    supertipos?: string[];
    /**
     * Clase de la instalación (IfcFacility de IFC 4.3: IfcBuilding, IfcBridge, IfcRoad, IfcRailway…) donde
     * está el tipo, según la estructura espacial del modelo. Varias o ninguna → null.
     */
    instalacion?: string | null;
  };
  /**
   * Nivel/planta al que pertenece este TIPO de elemento (etapa 2 de agrupación). Solo IFC/BIM lo
   * puebla, leído del modelo (IfcBuildingStorey vía IfcRelContainedInSpatialStructure): si todos los
   * elementos del tipo caen en un piso → su nombre; si cruzan varios → 'Varios niveles'; si el modelo
   * no lo dice → undefined. El DWG 2D no trae pisos, así que queda undefined ("Sin nivel" en la HP).
   */
  nivel?: string | null;
  /**
   * Ubicación/zona a la que pertenece este TIPO de elemento (etapa 2). IFC lo puebla desde el
   * IfcSpace que contiene los elementos (si el modelo lo trae); 'Varias zonas' si cruzan varios;
   * undefined si el modelo no lo dice. En DWG/IFC-sin-espacio la zona la infiere la IA desde la
   * descripción del concepto (fallback "Sin zona").
   */
  ubicacion?: string | null;
  /** Suma de longitudes de las polilíneas de la capa (unidades del dibujo). best-effort, 0 si no se pudo. */
  longitud?: number;
  /** Suma de áreas de los anillos cerrados de la capa (unidades² del dibujo). best-effort, 0 si no se pudo. */
  area?: number;
  /**
   * Detección AUTOMÁTICA de muro de doble trazo (Fase 2, geometría pura, sin IA ni usuario):
   * `corrido` = largo real del eje (quita el doble conteo de las dos caras) en unidades del dibujo;
   * `espesor` = separación entre caras del grupo dominante (unidades del dibujo) o null;
   * `doble_trazo_conf` = 0..1, qué tan nítido es el patrón (el front usa el corrido si es alta y
   * marca 'revisar' si es baja); `doble_trazo_pares` = nº de pares detectados con espesor real.
   */
  corrido?: number;
  espesor?: number | null;
  doble_trazo_conf?: number;
  doble_trazo_pares?: number;
  /**
   * Área RECONSTRUIDA cerrando los recintos de la capa (face-finding / polygonize, Fase 2), en
   * unidades² del dibujo. A diferencia de `area` (anillos cerrados EXPLÍCITOS), esta cierra los
   * recintos que se dibujan con líneas sueltas (pisos/losas/patios) donde ninguna polilínea está
   * cerrada. Es APROXIMADA: no descuenta vanos abiertos ni distingue planta de fachada/corte.
   * `recintos` = nº de recintos hallados; `recintos_conf` (0..1) = cobertura (fracción de la
   * longitud de aristas que cerró en algún recinto). best-effort, 0 si no cerró nada.
   */
  area_recintos?: number;
  recintos?: number;
  recintos_conf?: number;
  /**
   * Mejor cantidad MEDIBLE de la capa, ya en metros, con su confianza (Fase 2). Es el default honesto
   * del lector: la magnitud FINAL la fija la unidad del concepto al asignarlo (una capa de muros
   * sirve para ml de muro Y m² de aplanado). Opcional: solo el DWG/DXF 2D lo puebla.
   */
  cuantificacion?: PlanoCuantificacion;
  /**
   * DWG/DXF con varias vistas (paso 7): las medidas de arriba (`longitud`, `area`, `corrido`,
   * `recintos_*`, `cuantificacion`) son de las vistas que SUMAN (las plantas; una fachada dibujada en la
   * capa de muros ya no suma muro). `n_entidades` cuenta todas. Esto las desglosa por vista.
   */
  por_vista?: PlanoCapaEnVista[];
  /** Paso 6 (capas con patrón de muro): eje + vanos, en unidades del dibujo (lo que va × alto para m²). */
  eje_bruto?: number;
  /** Paso 6: vanos detectados en el muro, con el bloque de puerta/ventana que lo ocupa (si lo hay). */
  vanos?: PlanoVano[];
  /**
   * Eje tal como está dibujado, cuando hubo que quitarle el muro que el plano dibuja encima de una
   * ventana (`PlanoVano.muro_encima`); `corrido` ya viene sin eso. Ausente si no hubo.
   */
  corrido_dibujado?: number;
  /**
   * Pares de líneas paralelas de la capa que NO son muro: su separación no pertenece a una familia de
   * espesores con peso suficiente en la capa (muebles de doble línea, jambas). No suman al eje (`corrido`).
   * `largo` en unidades del dibujo.
   */
  otros_pares?: { n: number; largo: number } | null;
  /** Paso 6: segundo estimador del eje = huella cerrada ÷ espesor (muros como polilínea o con poché). */
  eje_por_huella?: number;
  /** Paso 5: cuántos anillos restaron como huecos y cuántos se ignoraron por repetidos (poché). */
  anillos?: { huecos: number; repetidos: number };
  /** Nombre sin el prefijo de xref, para mostrar (la clave sigue siendo `capa`). */
  capa_limpia?: string;
}

/** Un vano en un muro 2D (paso 6). `ancho`, `x`, `y` en unidades del dibujo. */
export interface PlanoVano {
  ancho: number;
  x: number;
  y: number;
  /** Nombre del bloque (puerta/ventana) cuyo tamaño coincide con el vano; null si no hay. */
  bloque: string | null;
  /**
   * Largo de muro que el plano dibuja ENCIMA de este vano (la ventana quedó bajo las dos caras del muro,
   * con sus jambas): es hueco, y ya está descontado de `corrido`. Ausente si no hubo.
   */
  muro_encima?: number;
  /**
   * true cuando no hay bloque pero el hueco trae trazos de puerta o ventana dibujados con líneas sueltas.
   * Solo sirve para nombrarlo; ausente en inventarios viejos (= «hueco sin puerta ni ventana»).
   */
  dibujado?: boolean;
}

/** Validación de las cotas contra la geometría (paso 8). */
export interface PlanoCotasValidacion {
  /** Cotas lineales del plano. */
  n: number;
  /** Las que miden un trazo del dibujo (sus dos orígenes caen sobre líneas). */
  asociadas: number;
  sueltas: number;
  /** Las que traen su número escrito a mano. */
  con_texto_propio: number;
  cuadran: number;
  /** Las que traen un número que NO es lo que mide su dibujo: fuera de escala o corregida a mano. */
  no_cuadran: number;
  no_cuadran_detalle: {
    escrito: string;
    escrito_m: number;
    medido_m: number;
    x: number;
    y: number;
    vista: string | null;
  }[];
}

/**
 * La mejor cantidad medible de una capa 2D (Fase 2), en metros, con su confianza. La emite el lector
 * de planos SOLO desde la geometría (sin nombres de capa ni tablas de dominio): muros de doble trazo →
 * corrido (ml), anillos cerrados o recintos reconstruidos → área (m²), longitud cruda → ml. Cuando no
 * hay escala o geometría medible, cae a 'a_mano' (degradación honesta: nada se inventa).
 */
export interface PlanoCuantificacion {
  /** Qué se pudo medir: lineal (ml), área (m²), o 'a_mano' cuando no se pudo. */
  magnitud: 'lineal' | 'area' | 'a_mano';
  /** Unidad de la cantidad ('ml' | 'm²'), o null en 'a_mano'. */
  unidad: 'ml' | 'm²' | null;
  /** Cantidad medida en METROS (aplicado el factor de escala), o null en 'a_mano'. */
  cantidad: number | null;
  /**
   * Qué tan fiable es: 'medida' = la geometría la fija bien; 'aproximada' = sale de la geometría pero
   * conviene revisarla (muro poco nítido, recinto sin cerrar vanos, longitud que cuenta dos caras);
   * 'a_mano' = no se pudo medir, el usuario la pone.
   */
  confianza: 'medida' | 'aproximada' | 'a_mano';
  /** Nota corta en español-MX de cómo se midió / qué revisar. La UI la muestra tal cual. */
  nota: string;
  /**
   * De dónde sale la cantidad. Se decide con la geometría, SIN el factor de escala: aunque la unidad la
   * conteste el usuario después, la pantalla aplica esta misma decisión. Ausente en inventarios viejos.
   */
  fuente?: PlanoFuenteDeMedida | null;
  /**
   * La mejor ÁREA de la capa aunque la cantidad principal sea lineal (el piso que encierran los muros),
   * en este orden: anillos explícitos → recintos cerrados con sus muros → recintos reconstruidos; en una
   * capa de muro (`fuente: 'eje_muro'`) los recintos cerrados van primero, porque ahí los anillos son la
   * huella del muro (poché, columnas). `cantidad` en m² (null sin escala). Ausente/null si no encierra área.
   */
  superficie?: {
    fuente: Exclude<PlanoFuenteDeMedida, 'eje_muro' | 'largo'>;
    confianza: 'medida' | 'aproximada';
    cantidad: number | null;
  } | null;
}

/**
 * De dónde mide el lector una capa 2D: el eje del muro de doble trazo, el área de anillos cerrados
 * dibujados, los recintos cerrados con sus muros (vanos puenteados), los recintos reconstruidos
 * (aproximados) o el largo crudo del trazo.
 */
export type PlanoFuenteDeMedida = 'eje_muro' | 'area_explicita' | 'recintos_medidos' | 'recintos_aprox' | 'largo';

/**
 * Unidades del dibujo, leídas de `$INSUNITS` del DXF. Todas las magnitudes del inventario van en
 * unidades del dibujo (no metros); esto captura de qué unidad se trata para la escala real (Fase 2).
 */
export interface PlanoUnidades {
  /** Código $INSUNITS del DXF (0 = sin unidad / desconocido). */
  codigo: number;
  /** Nombre legible ('mm' | 'cm' | 'm' | 'pulg' | 'sin unidad' | ...). */
  nombre: string;
  /** Factor para convertir a metro (ej. mm → 0.001), o null si es desconocido. */
  factor_a_metro: number | null;
}

/**
 * Naturaleza de una capa de un plano CAD, decidida por la IA a partir de sus RASGOS geométricos
 * (independiente del idioma; ver `clasificarCapasPlano` en el worker). Es la mitad que le faltaba a la
 * cuantificación 2D: la medición es geometría pura y no sabe si una capa es un muro o unos ejes.
 *
 * - `muro`: elementos verticales que encierran/dividen (los únicos medibles como muro).
 * - `vano` / `ventana`: puertas / ventanería embebidas en el muro.
 * - `instalacion`: redes de instalación dibujadas (hidráulica/sanitaria/drenaje/eléctrica/gas/HVAC/
 *   voz-datos). Es TRABAJO MEDIBLE, no ruido: su cantidad sale de la geometría (líneas → ml/m²;
 *   bloques → pza), igual que cualquier otra capa medible.
 * - `anotacion`: capas de texto (nombres de recinto, notas).
 * - `otro`: la IA la EXCLUYÓ de las anteriores (ejes/grid, mobiliario, cotas, marco…):
 *   geométricamente puede parecerse a un muro pero NO lo es → no se propone como muro medible.
 *
 * `null` = no hay clasificación para el plano (sin saldo de IA, falló, o plano viejo) → el consumidor
 * debe caer al comportamiento SIN semántica (idéntico al histórico). Nunca se hardcodea por nombre.
 */
export type NaturalezaCapa = 'muro' | 'vano' | 'ventana' | 'instalacion' | 'anotacion' | 'otro';

/**
 * Clasificación de las capas de un plano por IA, tal como viaja al front (DTO). Cada array lleva los
 * NOMBRES de capa del dibujo que la IA puso en esa categoría (rankeados, principal primero). Es el
 * espejo público de `project_planos.capas_clasificadas` (worker), sin el campo interno `fuente`.
 */
export interface CapasClasificadasDTO {
  wall: string[];
  door: string[];
  window: string[];
  /** Capas de instalación dibujadas (redes hidráulica/sanitaria/eléctrica/gas/HVAC…). Medibles. */
  installation: string[];
  /** Otra obra a cotizar que no es muro/vano/ventana/instalación (escaleras, pisos, bardas, jardinería…). Lista abierta. Opcional (esquema nuevo; DTOs cacheados viejos no la traen). */
  budgetable?: string[];
  text: string[];
  /** Ayudas de dibujo que NO se cotizan (ejes, cotas, cajetín, mobiliario, símbolos, hatch, elevaciones/cortes). Opcional (esquema nuevo). */
  noise?: string[];
  /** Por qué la IA mandó cada capa de `noise` a no cotizarse (capa → frase corta). Opcional (cachés viejas no lo traen). */
  motivos?: Record<string, string>;
  /** BLOQUES (por nombre) que SÍ se cotizan: puertas, muebles de baño/cocina, cancelería… Opcional (esquema nuevo). */
  bloquesObra?: string[];
  /** BLOQUES (por nombre) que son ruido: símbolos, mobiliario suelto, autos, vegetación… Opcional (esquema nuevo). */
  bloquesRuido?: string[];
  /**
   * Capas donde la IA NO pudo decidir con confianza entre obra y dibujo (nombre ambiguo + geometría
   * que no aclara, o una capa que la IA creyó muro pero la geometría no sostiene). Es la voz de la IA
   * diciendo "no sé, decide tú" → van a la cubeta "¿Esto se construye?" para que el usuario resuelva en
   * un paso. Opcional (esquema nuevo). NO la puebla el usuario, solo la IA. */
  uncertain?: string[];
  /** BLOQUES que la IA no pudo decidir (mismo criterio que `uncertain` para capas). Opcional. */
  bloquesDudosos?: string[];
  confianza: 'alta' | 'media' | 'baja' | null;
}

/** Qué se hace con una capa a la hora de presupuestar. La IA la decide; el usuario resuelve las dudosas. */
export type DecisionDeCapa = 'cotiza' | 'ruido' | 'dudoso';

/** Lo que la persona puede elegir con el ⋯ (la duda la propone solo la IA). Se guarda por plano (migr 166). */
export type DecisionDeCapaCorregida = Exclude<DecisionDeCapa, 'dudoso'>;

/**
 * Decisión de presupuesto de UNA capa, derivada de la clasificación de la IA (no muro-céntrica):
 *  - `cotiza`: trabajo a presupuestar (muro/vano/ventana/instalación u OTRA obra: escalera, piso, barda…).
 *  - `dudoso`: la IA NO pudo decidir con confianza → "¿Esto se construye?", lo resuelve el usuario en un paso.
 *  - `ruido`: no se cotiza (texto/anotación o ayudas de dibujo: ejes, cotas, cajetín, mobiliario…).
 * `null` cuando no hay clasificación (el consumidor cae al comportamiento sin semántica). Los `?.`
 * toleran DTOs cacheados con el esquema viejo (sin budgetable/noise/uncertain).
 * "por revisar" (dudoso) lo propone la IA (`uncertain`), NUNCA el usuario: el usuario solo mueve
 * directo entre cotiza/ruido (un paso).
 */
export function decisionDeCapa(
  capa: string,
  clasif: CapasClasificadasDTO | null | undefined,
): DecisionDeCapa | null {
  if (!clasif) return null;
  if (
    clasif.wall.includes(capa) ||
    clasif.door.includes(capa) ||
    clasif.window.includes(capa) ||
    clasif.installation?.includes(capa) ||
    clasif.budgetable?.includes(capa)
  ) {
    return 'cotiza';
  }
  if (clasif.uncertain?.includes(capa)) return 'dudoso';
  if (clasif.text.includes(capa) || clasif.noise?.includes(capa)) return 'ruido';
  // Lo que la IA no puso en ninguna lista → ruido (conservador: no cotizar lo desconocido; el usuario
  // lo rescata con el ⋯). La IA marca su duda EXPLÍCITA en `uncertain`, no dejándola fuera.
  return 'ruido';
}

/**
 * Decisión de presupuesto de UN BLOQUE (INSERT), por su nombre. Mismo criterio/cubetas que las capas
 * pero desde la clasificación de bloques de la IA: `cotiza` (puertas, muebles de baño/cocina…),
 * `dudoso` (la IA no supo, → "¿Esto se construye?") o `ruido` (símbolos, mobiliario suelto, autos…).
 * `null` sin clasificación. "por revisar" lo propone la IA (`bloquesDudosos`), no el usuario.
 */
export function decisionDeBloque(
  nombre: string,
  clasif: CapasClasificadasDTO | null | undefined,
): DecisionDeCapa | null {
  if (!clasif) return null;
  if (clasif.bloquesObra?.includes(nombre)) return 'cotiza';
  if (clasif.bloquesDudosos?.includes(nombre)) return 'dudoso';
  if (clasif.bloquesRuido?.includes(nombre)) return 'ruido';
  // Lo que la IA no ubicó → ruido; su duda explícita va en `bloquesDudosos`.
  return 'ruido';
}

/**
 * Deriva la naturaleza de UNA capa desde la clasificación de la IA. Puro y sin tablas de nombres: solo
 * mapea en qué array la puso el modelo. `null` cuando no hay clasificación (→ el consumidor usa el
 * comportamiento sin semántica). Una capa presente pero en ningún array = `otro` (la IA la excluyó).
 */
export function naturalezaDeCapa(
  capa: string,
  clasif: CapasClasificadasDTO | null | undefined,
): NaturalezaCapa | null {
  if (!clasif) return null;
  if (clasif.wall.includes(capa)) return 'muro';
  if (clasif.door.includes(capa)) return 'vano';
  if (clasif.window.includes(capa)) return 'ventana';
  // `?.` tolera clasificaciones CACHEADAS antes de agregar la categoría (JSONB sin la llave).
  if (clasif.installation?.includes(capa)) return 'instalacion';
  if (clasif.text.includes(capa)) return 'anotacion';
  return 'otro';
}

/**
 * ¿La naturaleza de una capa es TRABAJO MEDIBLE (se propone para presupuestar)? `muro/vano/ventana/
 * instalacion` sí; `anotacion/otro` no (ejes, mobiliario, cotas, texto → ruido). `null` (sin
 * clasificar) → false: no se afirma que sea presupuestable. Único lugar donde vive este criterio,
 * derivado de la semántica del enum (no de nombres de capa).
 */
export function naturalezaEsPresupuestable(nat: NaturalezaCapa | null | undefined): boolean {
  return nat === 'muro' || nat === 'vano' || nat === 'ventana' || nat === 'instalacion';
}

/**
 * Mediciones/datos que el plano TRAE pero que no se dibujan como geometría (Fase 2). Se cosechan
 * crudos, sin interpretar: la altura (m² de muros) suele venir en un atributo tipo `NIVELPLAFON`
 * ("N.PL.=2.45/npt"); las cotas son las medidas reales del proyectista; los textos, nombres de
 * local / áreas. Quién decide qué es la altura o cómo cruzar las cotas es una capa de arriba.
 */
export interface PlanoAnotaciones {
  /** Atributos vivos de los bloques (ATTRIB): tag→valor. Deduplicados. Ahí puede estar la altura. */
  atributos: { bloque: string; tag: string; valor: string }[];
  /** Textos del plano (TEXT/MTEXT): nombres de local, áreas anotadas, notas. Deduplicados. */
  textos: string[];
  /**
   * Cotas (DIMENSION): medidas reales del proyectista, EN UNIDADES DEL DIBUJO (aplicar la escala).
   * `valores` = número rotulado de cada cota (compat). `detalle` = cada cota con tipo/posición/
   * orientación (tope; inventarios viejos no lo traen).
   */
  cotas: { n: number; valores: number[]; detalle?: PlanoCotaDetalle[] };
}

/** Una cota (DIMENSION) del plano, cruda: la longitud geométrica y el número que el proyectista rotuló. */
export interface PlanoCotaDetalle {
  /** Tipo según el estándar DXF: 'rotada' | 'alineada' miden longitud; 'angular' | 'radio' | 'diametro' | ... no. */
  tipo: string;
  /** Longitud geométrica medida, en unidades del dibujo (`get_measurement`). */
  medida: number;
  /** Número rotulado que un humano lee sobre el plano = medida × DIMLFAC, en unidades del dibujo. */
  declarado: number;
  /** Posición representativa de la cota (punto medio del texto), en unidades del dibujo. null si no se pudo. */
  x: number | null;
  y: number | null;
  /** Orientación de la cota en grados (0 = horizontal), o null. */
  angulo: number | null;
}

/**
 * Escala REAL derivada de las COTAS rotuladas (unidad de dibujo → metro), independiente de `$INSUNITS`
 * y de nombres de capa/bloque. Resuelve planos que declaran una unidad pero están dibujados en otra
 * (p.ej. header dice mm pero el dibujo está en metros → `factor` ≈ 1.0). El consumidor prefiere este
 * `factor` sobre `PlanoUnidades.factor_a_metro` cuando `confianza` es alta.
 */
export interface PlanoEscalaPorCotas {
  /** Factor unidad-de-dibujo → metro derivado de las cotas, o null si no hubo cotas de longitud. */
  factor: number | null;
  /** 0..1: qué tan decisiva es la unidad ganadora frente a la 2ª (margen en décadas). */
  confianza: number;
  /** Unidad métrica elegida por el dato ('m' | 'cm' | 'mm'), o null. */
  unidad: string | null;
  /** Nº de cotas de longitud usadas. */
  n: number;
  /** Mediana del valor rotulado (unidades del dibujo), o null. */
  mediana_declarada: number | null;
  /** DIMLFAC efectivo (mediana). Normalmente 1. */
  dimlfac: number | null;
  /** true si el factor derivado coincide con el de `$INSUNITS`; false = el header miente; null = sin dato. */
  concuerda_insunits: boolean | null;
}

/**
 * UNIDAD del dibujo de un plano DWG/DXF, resuelta por el lector (`plano2svg.py`, `resolver_escala`) sin
 * IA. Se calcula al SUBIR el archivo (`project_files.escala_dibujo`) y otra vez al generar el
 * inventario (`PlanoInventario.escala`).
 *
 * Cómo se decide: se prueba cada unidad candidata (m, cm, mm y la del header si es otra) contra señales
 * del propio dibujo —extensión del conjunto, ancho de los bloques de puerta medido por su geometría,
 * espesor de muro del doble trazo, cota típica y cotas cuyo rótulo difiere de la geometría en una
 * potencia de 10— con rangos de plausibilidad de la calibración (`dwg_*`). Manda el `$INSUNITS` si da
 * una obra plausible; si no (o si falta), la ÚNICA candidata plausible. Si quedan dos o más, `dudosa`:
 * la pantalla le pregunta al usuario UNA vez por plano, ofreciendo SOLO las `opciones` (las plausibles).
 */
export interface PlanoEscala {
  estado: 'definida' | 'dudosa';
  /** Factor unidad-de-dibujo → metro. null si `dudosa` (no se mide hasta que el usuario elija). */
  factor: number | null;
  /** Unidad elegida ('m' | 'cm' | 'mm' | la del header). null si `dudosa`. */
  unidad: string | null;
  /**
   * De dónde salió: 'insunits' = el header del archivo, plausible; 'dibujo' = el header faltaba o era
   * imposible y el dibujo deja una sola unidad plausible; 'usuario' = la eligió el usuario. null si `dudosa`.
   */
  fuente: 'insunits' | 'dibujo' | 'usuario' | null;
  /** Por qué, en una frase (es-MX). Técnica: para el log o la ayuda, no para el cotizador. */
  motivo: string;
  /** Solo si `dudosa`: las unidades PLAUSIBLES, en orden de preferencia, con su consecuencia. */
  opciones: PlanoEscalaOpcion[];
  /** Lo que declara el header. */
  header: { unidad: string | null; factor: number | null };
  /** Diagnóstico: cada candidata con el veredicto de cada señal (staff / vara). */
  candidatas: PlanoEscalaCandidata[];
}

/** Una unidad posible para un plano con escala dudosa, con lo que implica elegirla. */
export interface PlanoEscalaOpcion {
  unidad: string;
  /** Nombre para mostrar ('metros' | 'centímetros' | 'milímetros' | …). */
  nombre: string;
  factor: number;
  /** Tamaño del dibujo entero con esta unidad, en metros. */
  ancho_m: number | null;
  alto_m: number | null;
  /** En palabras (es-MX): "Si está en metros: el dibujo mide 10 × 8 m, las puertas, 0.9 m." */
  consecuencia: string;
}

export type PlanoVeredictoSenal = 'plausible' | 'imposible' | null;

export interface PlanoEscalaCandidata {
  unidad: string;
  factor: number;
  senales: Record<
    'extension' | 'puertas' | 'muros' | 'cotas' | 'rotulo',
    { valor_m: number | null; veredicto: PlanoVeredictoSenal }
  >;
  imposibles: number;
  a_favor: number;
}

/** La unidad que el usuario eligió para un plano con escala dudosa (`project_files.escala_dibujo_elegida`). */
export interface PlanoEscalaElegida {
  unidad: string;
  factor: number;
  user_id?: string;
  at?: string;
  /**
   * «Cambió el plano»: la unidad NO la eligió la persona para este archivo; se copió del archivo de la hoja (su id) al
   * crear la revisión, para que las dos revisiones se lean con la MISMA unidad. Descartar la revisión la quita.
   */
  copiadaDe?: string;
}

/** Magnitud geométrica que hace falta para cuantificar un concepto del catálogo (Fase 2, la decide la IA). */
export type PlanoMagnitudTipo = 'conteo' | 'lineal' | 'area' | 'volumen' | 'peso' | 'otro';
/** Dato extra que la planta 2D no da y hay que sumar para llegar a la unidad del concepto. */
export const PLANO_INPUT_EXTRA_VALORES = ['ninguno', 'alto', 'espesor', 'alto_y_espesor', 'profundidad', 'peso_o_seccion'] as const;
export type PlanoInputExtra = (typeof PLANO_INPUT_EXTRA_VALORES)[number];

/** Cómo cuantificar un concepto: la IA clasifica su unidad → magnitud (m²=área, pza=conteo, kg=peso…). */
export interface PlanoMagnitudConcepto {
  magnitud: PlanoMagnitudTipo;
  /** ¿La magnitud se puede obtener de una planta 2D? (m² de muro sí, con alto; kg de acero no). */
  derivableDePlanta: boolean;
  inputExtra: PlanoInputExtra;
  razon: string;
  /**
   * La medida que el PROPIO concepto declara para pasar de la magnitud de su elemento a la suya, en metros
   * (el espesor promedio de un relleno «de 10 cm promedio», la profundidad de una excavación, el ancho de una
   * franja). null = el concepto no la dice. La lee la IA del texto; nada de tablas.
   */
  medidaDelConceptoM?: number | null;
}

/**
 * Concepto COMPLEMENTARIO / previo que la IA propone al asociar un concepto principal a un
 * elemento del plano (Fase 3): lo que el elemento implica pero el concepto no trae (un muro →
 * excavación, dala de desplante, cadena, castillos…). Es la NECESIDAD; el usuario la resuelve
 * después contra el catálogo (o APU-IA), con la cantidad derivada del elemento.
 */
export interface PlanoComplementario {
  /** Nombre corto de la necesidad, en español-MX de obra (ej. "Dala de desplante"). */
  necesidad: string;
  /** Frase para buscar el concepto real en el catálogo (mismo buscador/sugerencia). */
  consultaCatalogo: string;
  /** Unidad esperada del complementario (ml, m3, pza…), o null si no aplica. */
  unidadEsperada: string | null;
  /** De qué magnitud del elemento se deriva su cantidad (la FUENTE). */
  /** 'vanos' = por cada puerta o ventana que aloja el elemento (refuerzo de vanos, dintel, cabezal). */
  derivaDe: 'largo' | 'area' | 'conteo' | 'volumen' | 'vanos' | 'otro';
  /**
   * Magnitud de la cantidad que RESULTA (la de `unidadEsperada`), declarada por el modelo: con ella se
   * comprueba que el concepto elegido se cobre en la misma magnitud (sin tablas de unidades). null = no la dijo.
   */
  magnitud?: PlanoMagnitudTipo | null;
  /** Por qué el elemento lo implica (muy corto). */
  razon: string;
  /** true = casi siempre aplica; false = depende del contexto (el usuario decide). */
  tipico: boolean;
  /**
   * Multiplicador sobre la magnitud `derivaDe` para llegar a la cantidad del complemento (Fase 3,
   * la fija la IA — NO hay tabla concepto→fórmula). Ej.: un muro implica dala de desplante Y de
   * cerramiento → 2 sobre el largo; un aplanado va en las dos caras → 2 sobre el área; una
   * excavación de zanja → la sección (m²) sobre el largo. Ausente/≤0 = 1 (comportamiento previo).
   */
  factor?: number | null;
  /**
   * Dimensiones que la planta 2D NO dibuja y la IA tuvo que SUPONER para calcular `factor` (sección
   * de zanja, profundidad de cimiento, peralte, espesor de firme…). Su presencia marca la cantidad
   * derivada como SUPUESTO → el renglón entra 'revisar'. Es informativa (la aritmética usa `factor`);
   * el usuario ajusta la cantidad a mano. Vacío/ausente = la cantidad no depende de ningún supuesto.
   */
  dimensionesFaltantes?: PlanoDimensionFaltante[];
  /**
   * Solo con `derivaDe='largo'` sobre un muro con puertas/ventanas (la IA lo decide por complementario,
   * sin tabla): true = el complemento pasa por encima o por debajo de los huecos (cerramiento, cadena,
   * cornisa) y se mide con el largo CON huecos (eje bruto); false/ausente = se interrumpe en los huecos
   * (rodapié, zoclo) y se mide con el largo sin huecos (comportamiento previo).
   */
  incluyeVanos?: boolean;
  /**
   * Si el elemento está en varias plantas: en cuáles va este trabajo (sus rótulos, tal como vienen del
   * plano). La cadena de desplante va solo bajo la planta que se apoya en el terreno. Ausente = todas.
   */
  plantas?: string[];
  /** La cara del elemento que este trabajo necesita («abajo:terreno»), como la dijo la IA. */
  requiere?: string;
}

/** Una medida que el plano no dibuja y la IA supone para derivar la cantidad de un complementario. */
export interface PlanoDimensionFaltante {
  /** Qué se supuso, en español-MX (ej. "sección de zanja 0.40 × 0.60 m"). */
  nombre: string;
  /** Valor supuesto que entra en el factor (ej. 0.24). */
  valorSugerido: number;
  /** Unidad del valor supuesto (ej. "m²", "m"). */
  unidad: string;
}

/**
 * Instalación NO dibujada que la IA propone como SUPUESTO a partir de los recintos del plano (frente
 * #5·B, nivel proyecto). Una instalación no es "complementaria" de un elemento: es trabajo por
 * derecho propio que la planta arquitectónica no dibuja pero los cuartos implican (una casa con baño
 * y cocina lleva hidráulica/sanitaria; con estufa, gas; toda obra, eléctrica). La IA la infiere de los
 * NOMBRES de recinto del propio plano — sin tabla hardcodeada cuarto→instalación (Sebastián: "que
 * decida la IA; mi lista es opinión") — y NO propone la disciplina que ya está DIBUJADA (anti-doble).
 */
/**
 * Grupos de «Lo que el plano no dibuja», en el orden en que se muestran. `partida` = dónde entra en la
 * hoja (es un supuesto: siempre marcado para revisar). Agregar un grupo = una línea aquí + su frase en
 * el prompt de la IA (`proponerInstalacionesSupuesto`).
 */
export const GRUPOS_NO_DIBUJADO = [
  { id: 'preliminares', titulo: 'Preliminares', partida: 'Preliminares (supuesto)' },
  { id: 'provisional', titulo: 'Obra provisional y seguridad', partida: 'Obra provisional y seguridad (supuesto)' },
  { id: 'cimentacion', titulo: 'Cimentación', partida: 'Cimentación (supuesto)' },
  { id: 'estructura', titulo: 'Estructura', partida: 'Estructura (supuesto)' },
  { id: 'instalaciones', titulo: 'Instalaciones', partida: 'Instalaciones (supuesto)' },
  { id: 'pisos', titulo: 'Pisos y acabados', partida: 'Pisos y acabados (supuesto)' },
  { id: 'canceleria', titulo: 'Cancelería y carpintería', partida: 'Cancelería y carpintería (supuesto)' },
  { id: 'tramites', titulo: 'Trámites y permisos', partida: 'Trámites y permisos (supuesto)' },
  { id: 'cierre', titulo: 'Cierre y pruebas', partida: 'Cierre y pruebas (supuesto)' },
] as const;
export type GrupoNoDibujado = (typeof GRUPOS_NO_DIBUJADO)[number]['id'];

export interface PlanoInstalacionSupuesta {
  /** A qué grupo de «Lo que el plano no dibuja» pertenece (lo decide la IA; por defecto instalaciones). */
  grupo: GrupoNoDibujado;
  /** Disciplina en es-MX (eléctrica, hidráulica, sanitaria, gas, HVAC…). Lista ABIERTA: la decide la IA. */
  disciplina: string;
  /** Nombre corto (2 a 5 palabras) para listas y títulos: "Salidas de contacto". */
  nombre: string;
  /** Concepto de obra COMPLETO, en es-MX (una partida de presupuesto, no su desglose). */
  descripcion: string;
  /** Frase para buscarlo en el catálogo (mismo buscador/sugerencia que el resto de la HP). */
  consultaCatalogo: string;
  /** Unidad esperada (salida, pza, ml, lote…), o null si no aplica. */
  unidadEsperada: string | null;
  /** Qué recintos/contexto la implican (una frase; el usuario la lee para decidir si aplica). */
  razon: string;
  /**
   * Cantidad ESTIMADA por la IA con lo que el plano trae (nombres y número de recintos, muebles
   * dibujados), o null si con eso no se puede estimar (la UI la pide: "a definir"). Es un supuesto.
   */
  cantidad: number | null;
  /** De dónde sale la cantidad, en una frase que el usuario lee ("una por cuarto: 14 cuartos"). '' si no hay. */
  deDonde: string;
  /**
   * Lo estructural: con qué da cada cara («arriba:intemperie», «abajo:cuarto»), decidido viendo la obra
   * completa. Sus complementarios se filtran con ESTAS caras, no con las que adivine cada pedido suelto.
   */
  caras?: string[];
}

/** Altura de muro/entrepiso propuesta por la IA desde lo cosechado del plano (para el m² de muros). */
export interface PlanoAlturaPropuesta {
  /** Altura propuesta en metros, o null si no se halló en el plano. */
  alturaM: number | null;
  /** Todas las alturas plausibles detectadas (m), por si hay varias por zona. */
  candidatas: number[];
  fuente: string;
  razon: string;
  /**
   * DWG con varias plantas cuya elevación trae entrepisos distintos (`PlanoAlturaDeVista.origen =
   * 'por_desempatar'`): la altura de cada planta que eligió la IA ENTRE las candidatas, leyendo los
   * rótulos de las vistas. Por id de vista. Ausente si no hubo nada que desempatar.
   */
  porVista?: Record<string, { alturaM: number; razon: string }>;
}

/**
 * Una cantidad ESTIMADA transcrita de una lámina leída por visión (PDF→planos). NO sale de la
 * geometría (un PDF no la tiene): es lo que el proyectista escribió en la lámina ("Cisterna 2.800 m³"),
 * leído tal cual. Por eso es aproximada y la UI la marca como estimación. Se guarda cruda —sin
 * clasificar la magnitud por la unidad, que en este repo la decide la IA (`PlanoMagnitudConcepto`)—.
 */
export interface PlanoCantidadEstimada {
  /** Qué mide, en las palabras de la lámina (ej. "Cisterna de ingreso"). */
  que: string;
  /** Valor numérico transcrito. */
  valor: number;
  /** Unidad tal como la escribió la lámina (m³, ml, pza…), o null si no venía. */
  unidad: string | null;
  /** Página de la lámina del PDF de la que se leyó (para citar la fuente). */
  laminaPagina?: number;
}

/**
 * De dónde salió el inventario de un plano, y cuánto confiar en sus cantidades:
 *  - 'cad'        : DWG/DXF (geometría real) → cantidades exactas.
 *  - 'ifc'        : modelo IFC nativo (geometría 3D) → cantidades exactas.
 *  - 'pdf_vision' : un PDF leído por VISIÓN → cantidades ESTIMADAS (sin geometría; pueden no ser exactas).
 */
export type PlanoOrigen = 'cad' | 'ifc' | 'pdf_vision';

/** Inventario del plano: bloques (contables) + capas (geometría). */
export interface PlanoInventario {
  bloques: PlanoBloque[];
  capas: PlanoCapa[];
  total_inserts: number;
  tipos_de_bloque: number;
  /** Unidades del dibujo ($INSUNITS). Opcional: inventarios viejos no lo traen. */
  unidades?: PlanoUnidades;
  /**
   * Compat: estadística de las cotas. Desde el paso 2 del plan de medición DWG su `factor` es el de
   * `escala` (con confianza 1 si quedó definida); en inventarios viejos era la heurística "la cota
   * típica ronda 1 m". Leer la unidad de `escala`, no de acá.
   */
  escala_por_cotas?: PlanoEscalaPorCotas;
  /** Unidad del dibujo resuelta por el lector (paso 2). Opcional: inventarios viejos no lo traen. */
  escala?: PlanoEscala;
  /** Vistas del archivo (paso 7) con su altura por planta (paso 9). Opcional: inventarios viejos no lo traen. */
  vistas?: PlanoVista[];
  /**
   * Paso 4: bloques dentro de bloques (vistas que suman), con sus padres. No se suman a `bloques`:
   * contar el baño o su inodoro lo decide quien cotiza.
   */
  bloques_anidados?: { nombre: string; n: number; dentro_de: string[] }[];
  /** Paso 4: capas congeladas o apagadas (no se ven en el CAD): no se midieron ni contaron. */
  capas_ocultas?: { capa: string; n_entidades: number }[];
  /** Paso 8: cotas contra la geometría. null si la escala es dudosa. */
  cotas_validacion?: PlanoCotasValidacion | null;
  /** Mediciones/datos no dibujados (atributos, cotas, textos). Opcional: inventarios viejos no lo traen. */
  anotaciones?: PlanoAnotaciones;
  /**
   * true = el inventario NO salió de la geometría: se leyó un PDF por VISIÓN y sus cantidades son
   * ESTIMACIONES (transcritas de la lámina). La UI DEBE avisarlo — Sebastián, opción (a): un plano en
   * PDF sí cuantifica, pero avisando que la medición puede no ser exacta.
   */
  estimadoVision?: boolean;
  /**
   * Cantidades ESTIMADAS leídas de las láminas del PDF por visión (solo cuando `estimadoVision`). Es lo
   * que un PDF sí aporta a la HP a falta de geometría seleccionable. Vacío/ausente en planos CAD/IFC.
   */
  cantidadesEstimadas?: PlanoCantidadEstimada[];
  /**
   * Solo IFC: el esquema del archivo y las instalaciones (IfcFacility) que trae, para proponer el rol
   * (un puente o una carretera mide sitio, no edificio) y avisar de un esquema preliminar.
   */
  ifc?: {
    esquema?: string | null;
    /** Clases de IfcFacility presentes: IfcBuilding, IfcBridge, IfcRoad, IfcRailway, IfcMarineFacility… */
    instalaciones?: string[];
    /** Aviso para la persona (esquema preliminar, lectura parcial…). null = nada que avisar. */
    aviso?: string | null;
    /**
     * Materiales que el modelo no tipifica (sin categoría ni densidad) y que se reconocieron por su
     * nombre, con cuántas piezas pesan así. Para «Lo que el plano no traía y se dedujo».
     */
    materialesDeducidos?: import('./materialIfcDeducido.js').MaterialDeducidoIfc[];
    /** Si no se pudo reconocer (IA caída), el texto único para avisarlo. null = nada que avisar. */
    avisoMateriales?: string | null;
  };
  /**
   * Solo DWG/DXF: la IA no pudo decir qué es cada capa al leer este plano (por qué, en la categoría
   * client-safe de `avisoIa`). Las capas se miden solo por su forma (ejes incluidos) → la pantalla lo
   * avisa. Ausente = la clasificación salió, se reusó de la caché, o el plano es de antes.
   */
  clasificacion_capas_fallo?: import('./avisoIa.js').MotivoFalloIa;
}

/** Estado del pipeline DWG/DXF → SVG de un plano (fila en `project_planos`). */
export type PlanoStatus = 'pending' | 'ready' | 'failed';

/**
 * ROL de un ARCHIVO/plano dentro de la HP POR-PROYECTO. Un proyecto de planos es la UNIÓN de
 * VARIOS DWG que se COMPLEMENTAN: unos APORTAN cantidad (planta de arquitectura, urbanización) y
 * otros solo ENRIQUECEN (láminas de detalles/especificaciones). El rol decide si el archivo suma
 * cantidad a la HP o solo aporta contexto, y sirve para no doble-contar entre planos.
 *
 * - `cuantificable_edificio`: el archivo mide obra del edificio (plantas, cortes con cantidades).
 * - `cuantificable_sitio`: el archivo mide obra de sitio/urbanización (terracerías, redes, exteriores).
 * - `referencia_spec`: lámina de detalles/especificaciones — NO aporta cantidad, solo enriquece.
 *
 * NO se deriva por el nombre del archivo (nada de tabla de sufijos ARQ/DET/URB): se propone por el
 * CONTENIDO del inventario (ver `derivarRolArchivoSugerido`) y el usuario puede cambiarlo.
 */
export type PlanoRolArchivo = 'cuantificable_edificio' | 'cuantificable_sitio' | 'referencia_spec';

/** Estado + resultado del plano de un archivo (una fila por (project, file)). */
export interface ProjectPlano {
  id: string;
  projectId: string;
  fileId: string;
  status: PlanoStatus;
  inventario: PlanoInventario | null;
  /** Rol del archivo dentro de la HP por-proyecto (aporta cantidad vs solo referencia). */
  rol: PlanoRolArchivo;
  /** De dónde salió el inventario (cad/ifc = exacto; pdf_vision = estimado). Default 'cad'. */
  origen?: PlanoOrigen;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

// ============ Geometría desde la HP (PROTOTIPO staff-only) ============

/**
 * Estado del pipeline "de la HP a la geometría" (una fila por cotización en
 * `quote_geometria`). A partir de los conceptos de la Hoja de Presupuesto genera
 * un plano 2D (DXF + SVG), un modelo 3D BIM (IFC) y un render (PNG).
 * Feature OCULTA / staff-only + flag GEOMETRIA_HP_ENABLED.
 */
export type GeometriaHpStatus = 'running' | 'completed' | 'failed';

/** Estado del render por IA (fotos + animación). 'none' = nunca se generó. */
export type GeometriaRenderStatus = 'none' | 'running' | 'completed' | 'failed';

/**
 * Ficha del edificio deducida por IA a partir de los conceptos de la HP. Alimenta
 * el prompt de la foto. Todos los campos son texto libre que devuelve el modelo
 * (nada de tablas de materiales hardcodeadas). `estilo_sugerido` es una propuesta,
 * el usuario elige el estilo final.
 */
export interface GeometriaFicha {
  /** Tipología (ej. "vivienda unifamiliar", "oficinas", "comercial", "educativo"). */
  tipologia: string | null;
  /** Nº de niveles/pisos deducido de la HP (texto libre, ej. "1 nivel"). */
  niveles: string | null;
  /** Rasgos de volumetría (ej. "doble altura, voladizo, retranqueos"). */
  volumetria: string | null;
  /** DRIVER #1: material dominante de fachada (ej. "tabique aparente"). */
  fachada_material: string | null;
  /** Material de acento de fachada (ej. "cantera", "madera", "celosía"). */
  fachada_acento: string | null;
  /** Textura / acabado de fachada (ej. "liso mate", "aparente"). */
  fachada_textura: string | null;
  /** Cubierta (ej. "losa plana", "azotea verde", "inclinada", "bóveda"). */
  cubierta: string | null;
  /** Estilo/partido arquitectónico que propone la IA (el usuario elige el final). */
  estilo_sugerido: string | null;
}

/**
 * Estilos arquitectónicos que ofrece el selector de la foto. Presets creativos del
 * prototipo (fragmentos de prompt en el pipeline); el usuario también puede tomar
 * el estilo sugerido por la IA. No es una tabla de dominio del negocio.
 */
export const GEOMETRIA_ESTILOS = [
  { id: 'moderno', label: 'Moderno' },
  { id: 'contemporaneo', label: 'Contemporáneo' },
  { id: 'minimalista', label: 'Minimalista' },
  { id: 'colonial', label: 'Colonial mexicano' },
  { id: 'mediterraneo', label: 'Mediterráneo' },
  { id: 'industrial', label: 'Industrial' },
] as const;

/** Estado + resultado de la geometría de una cotización. */
export interface QuoteGeometria {
  quoteId: string;
  status: GeometriaHpStatus;
  /** Rutas del propio API para descargar cada artefacto; null hasta estar listo. */
  dxfUrl: string | null;
  svgUrl: string | null;
  ifcUrl: string | null;
  renderUrl: string | null;
  timingsMs: Record<string, number> | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * La selección del plano que originó un renglón de la HP: un tipo de bloque
 * ("todos los iguales") o una capa (geometría suelta: muros/pisos/vanos).
 * `clave` = nombre efectivo del bloque, o nombre de la capa. `ids` = los
 * `data-id` del SVG resaltados (para el resaltado bidireccional plano ↔ HP).
 */
export interface PlanoSeleccion {
  tipo: 'bloque' | 'capa';
  clave: string;
  ids?: string[];
  /**
   * DWG/DXF con varias vistas: la capa SOLO dentro de esa vista (`PlanoVista.id`, p. ej. la planta
   * baja). Ausente = la capa completa (las plantas que suman). Es parte de la identidad del renglón:
   * "A-MUROS · Planta baja" y "A-MUROS · Planta alta" son dos principales distintos.
   */
  vista?: string;
  /**
   * Un renglón por CONCEPTO («Presupuestar todo el plano» junta los tipos que llevan el mismo): los OTROS
   * elementos del plano que este renglón también cubre. La identidad del renglón sigue siendo la de arriba;
   * para «qué tiene concepto» y «¿Qué me falta?» cuentan todos (`seleccionesDe`). Ausente = uno solo.
   */
  ademas?: Omit<PlanoSeleccion, 'ademas'>[];
}

/** Todos los elementos del plano que cubre un renglón: su selección y, si junta varios, los demás. */
export function seleccionesDe(r: { seleccion?: PlanoSeleccion | null }): Omit<PlanoSeleccion, 'ademas'>[] {
  const s = r.seleccion;
  if (!s) return [];
  const { ademas, ...base } = s;
  return [base, ...(ademas ?? [])];
}

/**
 * Un renglón de la Hoja de Presupuesto armado desde una selección del plano a
 * la que se le asignó A MANO un concepto del catálogo. Agrupado por `partida`
 * (= familia del concepto; 'Sin clasificar' si no trae familia). `precioUnitario`
 * / `importe` son null cuando el concepto elegido no alcanza para dar precio
 * (un catálogo pelado sin desglose: no se fuerza un importe 0).
 */
export interface PlanoRenglon {
  id: string;
  planoId: string;
  /** Cotización interna del plano: el renglón es un quote_concept de ella (panel de análisis). */
  quoteId: string;
  projectId: string;
  /** Archivo/plano del que salió este renglón (identidad del DWG/IFC fuente dentro del proyecto). */
  sourceFileId: string | null;
  /** Nombre del archivo fuente, para mostrar de qué plano viene cada renglón en la HP por-proyecto. */
  sourceFileName: string | null;
  /** Rol del archivo fuente en la HP por-proyecto (heredado de `project_planos.rol`). */
  planoRol: PlanoRolArchivo;
  /**
   * ¿Este renglón APORTA cantidad al total de la HP? false para renglones de un archivo con rol
   * `referencia_spec` (solo enriquece). Es el gancho anti-doble-conteo (deriva de `planoRol`).
   */
  aportaCantidad: boolean;
  partida: string;
  catalogId: string | null;
  resourceClave: string | null;
  conceptoDescripcion: string;
  unidad: string | null;
  cantidad: number;
  /**
   * Lo que midió el plano, si el cotizador corrigió la cantidad a mano (`cantidad` es la suya).
   * null/ausente = la cantidad es la medida.
   */
  cantidadMedida?: number | null;
  precioUnitario: number | null;
  importe: number | null;
  /** 'catalogo' (concepto elegido a mano) | 'apu_ia' (reservado, Fase 3). */
  origen: string;
  /** 'principal' (el concepto del elemento) | 'complemento' (un previo/acabado que implica). */
  rol: string;
  /** true = la cantidad se midió automático con detección dudosa → el ingeniero debe revisarla. */
  revisar: boolean;
  /** Estado del APU sintético (IA) de este renglón, cuando el concepto no está en catálogo:
   *  null = no aplica; 'queued'|'running' = generándose; 'done' = listo; 'failed' = falló. */
  apuIaStatus: 'queued' | 'running' | 'done' | 'failed' | null;
  /** Semáforo de confianza del sintético ('green'|'yellow'|'red') o null si no hay sintético. */
  apuIaConfianza: 'green' | 'yellow' | 'red' | null;
  /** Detalle TÉCNICO del fallo del sintético. Solo log/staff: NUNCA a la vista del cliente
   *  (la pantalla muestra `mensajeDeFalloIa(apuIaFailReason)`). */
  apuIaError: string | null;
  /** Categoría CLIENT-SAFE del fallo ('ai_unavailable' = la IA no respondió | 'other'); null si no falló. */
  apuIaFailReason?: 'ai_unavailable' | 'other' | null;
  /** Toggle "generar con IA": true = marcado para que el lote lo genere como sintético.
   *  Un concepto que entró por descripción (sin catálogo) nace marcado. */
  apuIaRequested: boolean;
  seleccion: PlanoSeleccion | null;
  /** Complementario: id del renglón PRINCIPAL del que cuelga (la hoja lo muestra indentado debajo). */
  principalId?: string | null;
  /** Complementario que piden varios principales: los OTROS de los que sale (además de `principalId`). */
  padres?: string[];
  /**
   * Números generadores: la FOTO de la medición que justifica la cantidad (migr 177). null = el renglón
   * se agregó antes de que existieran (el generador lo dice).
   */
  generador?: import('./numerosGeneradores.js').FotoGenerador | null;
  /**
   * Clasificación del concepto en los ejes de agrupación de la HP (la calcula la IA una vez y se
   * cachea). null = aún sin clasificar (se pide al elegir un eje distinto de "familia"). La partida
   * por default (`partida`) es la familia; los otros ejes salen de aquí.
   */
  agrupacion: PlanoAgrupacion | null;
  /**
   * Especificaciones de láminas de detalle (`referencia_spec`) VINCULADAS a este concepto por el
   * emparejamiento detalle→concepto. Enriquecen la descripción del concepto — NUNCA su cantidad.
   * Se pegan solas (cada cambio de renglón o de rol de un plano las recalcula; el usuario puede quitar
   * una y no vuelve). Se persiste en `plano_concepto_meta.spec_refs`.
   */
  specRefs: EspecificacionVinculada[];
  /**
   * «Cambió el plano»: `quote_concepts.apu_ia_costo_directo` y `apu_ia_importe` del análisis con IA (para
   * calcular el importe con otra cantidad, como `fijarCantidad`). null = sin análisis con IA.
   */
  apuIaCostoDirecto?: number | null;
  apuIaImporte?: number | null;
  /** Cómo se cuantifica el concepto (guardado al ponerlo). null = no se guardó (renglón de antes). */
  magnitudConcepto?: Pick<PlanoMagnitudConcepto, 'magnitud' | 'inputExtra'> | null;
  /** La cantidad que da una revisión nueva del plano y no se aplicó sola («Usar la cantidad nueva»). */
  propuestaRevision?: import('./revisionDePlano.js').PropuestaDeRevision | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Una especificación de un archivo de detalle (`referencia_spec`) VINCULADA a un concepto de la HP.
 * Es enriquecimiento de la spec/descripción/APU del concepto — NUNCA cambia su cantidad. Se persiste
 * en `plano_concepto_meta.spec_refs` (array) y lo produce el emparejamiento detalle→concepto
 * (`@supernova/quotes` → `vincularDetallesConConceptos`).
 */
export interface EspecificacionVinculada {
  /** Identidad de la spec dentro de su lámina de detalle (`${fileId}#${idx}`). */
  detalleId: string;
  /** El texto de la especificación tal como está en la lámina ("BLOQUES DE HORMIGON 19x19x39"). */
  texto: string;
  /** Cómo se emparejó: por rótulo/llamada, por descripción, o quedó como spec disponible. */
  via: 'callout' | 'descripcion' | 'equivalente' | 'general' | 'sin_vinculo';
  /** Confianza del vínculo (para que el ingeniero sepa cuáles revisar). */
  confianza: 'alta' | 'media' | 'baja';
  /** 0..1: 1.0 en callout (código idéntico); el score léxico en descripción. */
  score: number;
  /** Archivo/plano de detalle del que salió la spec, para trazarlo en la HP. */
  archivo: string | null;
}

/**
 * Ejes por los que se puede agrupar la HP del plano. La partida GENERAL siempre es el nombre del
 * proyecto; el eje elegido define las subpartidas. 'familia' es el default (sale del concepto, sin
 * IA). Los demás los infiere la IA por concepto (etapa 1: especialidad/cronologico/sistema/material;
 * etapa 2: nivel/ubicacion, que dependen de DÓNDE está el elemento, no del concepto).
 */
export type TipoAgrupacion =
  | 'familia'
  | 'especialidad'
  | 'cronologico'
  | 'sistema'
  | 'material'
  | 'nivel'
  | 'ubicacion'
  | 'obra';

/** Partida elegida para un concepto dentro de una obra de referencia (cache por obra, spec obra-referencia §3). */
export interface PartidaDeObraAsignada {
  /** Hoja elegida; null = la IA dijo que ninguna partida corresponde («Sin partida»). */
  nodoId: string | null;
  /** huellaDeObra() del árbol con el que se decidió. Distinta a la actual → vieja. */
  huella: string;
  /** huellaCorta(claveDeDescripcion(descripción)) del concepto cuando se decidió. Distinta → vieja. */
  desc: string;
}

/** Clasificación de un concepto en los ejes de agrupación (cacheada por renglón). */
export interface PlanoAgrupacion {
  /** Oficio / capítulo tipo ECOSTOS: "Albañilería", "Cimentaciones", "Instalación eléctrica"… */
  especialidad: string;
  /** Fase del proceso constructivo (vocabulario controlado para agrupar estable). */
  faseObra: string;
  /** Orden de la fase (1=primero) para ordenar cronológicamente las subpartidas. */
  ordenFase: number;
  /** Sistema constructivo (más grueso que especialidad): Estructura/Envolvente/Instalaciones/Acabados/Preliminares. */
  sistema: string;
  /** Material predominante: Concreto/Acero/Mampostería/Madera/Mixto/… */
  material: string;
  /** Nivel/planta (etapa 2; IFC = IfcBuildingStorey, DWG = IA). null = sin determinar. */
  nivel?: string | null;
  /** Ubicación/zona (etapa 2; IFC = espacio, DWG = IA). null = sin determinar. */
  ubicacion?: string | null;
  /** Partida por obra de referencia: obraId → asignación. Una hoja puede probarse con varias obras. */
  obras?: Record<string, PartidaDeObraAsignada>;
}

// ============ Reprecio del catálogo por ZONA (T3) ============

/** Estados de una corrida de reprecio del catálogo del cliente por zona. */
export type CatalogRepriceStatus =
  | 'queued'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed';

/** Órdenes de control de una corrida (pausa cooperativa, igual que las cotizaciones). */
export type CatalogRepriceControl = 'run' | 'pause' | 'stop';

/**
 * Estado de una corrida de reprecio del catálogo por zona.
 *
 * El precio original del cliente (`catalog_resources.precio`) NO se toca: la
 * versión zonificada vive aparte (tabla `catalog_zone_prices`). Esta fila es el
 * estado del proceso que la construye.
 */
export interface CatalogRepriceRun {
  id: string;
  catalogId: string;
  /** Proyecto del que se leyó la zona (si vino de una obra). */
  projectId: string | null;
  /** Zona objetivo (estado/ciudad, texto libre). */
  zona: string;
  status: CatalogRepriceStatus;
  control: CatalogRepriceControl;
  /** Insumos atómicos a reprecar (materiales, mano de obra, equipo). */
  totalAtomicos: number;
  /** Atómicos ya resueltos. */
  procesados: number;
  /** Búsquedas de mercado REALES (tras deduplicar insumos idénticos). */
  comprados: number;
  /** Atómicos resueltos por dedup o caché fresca (sin gastar créditos). */
  reusados: number;
  /** Compuestos (auxiliares/conceptos) recalculados desde sus componentes. */
  compuestosRecalculados: number;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

// ============ Procedimiento constructivo (por concepto de APU-IA) ============
// Documento HTML autocontenido, imprimible, para el residente. Se genera por
// concepto sintético (con `apu_ia_matrix`). Una fila por concepto en
// `concept_procedimiento`; se invalida cuando cambia el análisis de origen.

export type ProcedimientoEstado = 'idle' | 'running' | 'completed' | 'failed';

/** De dónde salió el croquis (sección 1). Diagnóstico interno, no se muestra al cliente. */
export type ProcedimientoCroquisSource = 'plantilla' | 'modelo' | 'ninguno';

/**
 * Variante del procedimiento. Hoy hay UN solo camino: 'modelo' — el modelo dibuja
 * la LÁMINA técnica completa en una llamada (croquis + secuencia + detalles +
 * despiece + paso a paso + detalles técnicos). Se conserva el tipo (y el valor
 * histórico 'plantilla') por compatibilidad de la caché/ruta; ya no bifurca.
 */
export type ProcedimientoVariante = 'plantilla' | 'modelo';

/**
 * Variante única / por DEFECTO (pre-generación al aprobar el APU y lo que pide la
 * UI). Un solo lugar para cambiarla.
 */
export const PROCEDIMIENTO_VARIANTE_DEFAULT: ProcedimientoVariante = 'modelo';

/** Estado + documento del procedimiento constructivo de un concepto (por variante). */
export interface ProcedimientoConstructivo {
  conceptId: string;
  variante: ProcedimientoVariante;
  status: ProcedimientoEstado;
  /** HTML autocontenido imprimible. null mientras no esté 'completed'. */
  html: string | null;
  /** Familia detectada (zapata | losa | …). Diagnóstico. */
  familia: string | null;
  /** Origen del croquis. Diagnóstico. */
  croquisSource: ProcedimientoCroquisSource | null;
  error: string | null;
  generatedAt: string | null;
  /**
   * true cuando el APU cambió respecto del documento en caché: el documento que
   * viaja (si viaja) es de una versión anterior y se está regenerando.
   */
  stale: boolean;
}
